import "./MyFirstRealVibe.scss";

import { VibeMessage } from "../components";
import PostWrapper from "../PostWrapper";
import { PostCategory } from "../postCategories";

function MyFirstRealVibeScreen() {
  return (
    <PostWrapper
      category={PostCategory.VibeCoding}
      subTitle="Talking back and forth with my robot colleague"
      title="My first real vibe"
    >
      <p>
        I've been using AI tools for a while, but never truly in the "vibe" way.
      </p>

      <p>
        My initial use of AI tools has been more specific prompts for a known
        task, creating a small update that was easy to slot into my existing
        code.
      </p>

      <p>
        Or the "god-mode" level of auto-complete through prompting via inline
        comments or method names and having auto-complete generate pretty solid
        first drafts of new code. Which is extra awesome when it comes to
        writing tests (...or not writing tests, just writing test cases and
        having AI generate the whole test 🎉)
      </p>

      <p>This was different.</p>

      <p>
        This was a session that felt more like collaboration than prompting. It
        was fun.
      </p>

      <p>
        I did still review the changes at every step, and (when I remembered) I
        kept the commits separate so it's easy to follow along in that way as
        well. (I'll add in some links below to those commits). So, maybe not yet
        the full "vibe and ride" that it seems others are doing, but vibing
        nonetheless.
      </p>

      <p>What follows is the vibe conversation that did the following:</p>

      <ul>
        <li>
          Removed the auto-checking of the cook mode option (since I realized
          that may annoy some users 😅)
        </li>
        <li>Created the base setup for the Useful Tools & Code section</li>
        <li>Noticed a thing it added for categories that I liked</li>
        <li>
          Vibed back and forth about categories, making things reusable, adding
          in functionality (filtering), and implementing that for posts and
          tools sections
        </li>
        <li>
          Had an image made for the empty state of filtering to a category with
          no pages... just because I was curious how that would go
        </li>
        <li>Created this post</li>
      </ul>

      <p>Here are those links to the commits:</p>
      <ul>
        <li>
          <a
            href="https://github.com/lrcrews/ryancrews.com.react/commit/f5595801b9a660c123c015585177ffde40151be8"
            target="_blank"
            rel="noopener noreferrer"
          >
            don't enable cook mode by default
          </a>
        </li>
        <li>
          <a
            href="https://github.com/lrcrews/ryancrews.com.react/commit/a7d994738af3543e9a194915e1744601083411af"
            target="_blank"
            rel="noopener noreferrer"
          >
            useful tools initial setup
          </a>
        </li>
        <li>
          <a
            href="https://github.com/lrcrews/ryancrews.com.react/commit/a14af022ff872c4f4674a46164d05e42bce40365"
            target="_blank"
            rel="noopener noreferrer"
          >
            add category section to posts home too
          </a>
        </li>
        <li>
          <a
            href="https://github.com/lrcrews/ryancrews.com.react/commit/8a4865c2981142cb0516f5eafe8b30d0c47e3fad"
            target="_blank"
            rel="noopener noreferrer"
          >
            empty content state
          </a>
        </li>
        <li>
          <a
            href="https://github.com/lrcrews/ryancrews.com.react/commit/4f4b13e9194344c32a56fa2afe722bdcdfdec6ec"
            target="_blank"
            rel="noopener noreferrer"
          >
            vibe code post
          </a>
        </li>
      </ul>

      <h3>Everything written below was generated by OpenAI Codex</h3>

      <p>
        NOTE: I did update the prompts from my side to match my actual prompts
        (Codex made some adjustments when it wrote them 🤷).
      </p>

      <p>
        The useful part of this session was not one single command. It was the
        rhythm: Ryan described what he wanted, Codex inspected the code, made a
        small change, verified it, and then adjusted when Ryan pushed the idea a
        little further. That back-and-forth is where the work started to feel
        like collaboration instead of a one-shot prompt.
      </p>

      <div className="vibe-thread" aria-label="Conversation highlights">
        <VibeMessage from="Ryan">
          Remove the auto-checking of the cook mode option (so that the user
          must specifically enable it themselves). Keep in the code turning cook
          mode off when they leave a recipe page
        </VibeMessage>
        <VibeMessage from="Codex">
          I changed the footer effect so route changes can only disable cook
          mode, while the checkbox remains the only way to enable it.
        </VibeMessage>

        <VibeMessage from="Ryan">
          you said "it only turns off when the cook mode toggle should no longer
          be shown.", but will also turn off when the user unchecks the input
          (after turning it on first)?
        </VibeMessage>
        <VibeMessage from="Codex">
          Yes. The user can still turn it off manually. My wording was
          imprecise: “only” meant the automatic route-based effect only turns it
          off now, not that manual unchecking was removed.
        </VibeMessage>

        <VibeMessage from="Ryan">
          Create the path and related components for USEFUL_TOOLS_AND_CODE so
          that we are ready to add content pages. Add a few categories to start
          with, like "CSS snippet", "utility method", "tool", and anything else
          along those lines that makes sense for this.
          <br />
          <br />
          Use the existing setup of POSTS_HOME_PATH and the related Blog Posts
          components as a reference.
        </VibeMessage>
        <VibeMessage from="Codex">
          I mirrored the blog post setup with a new route, home screen, wrapper,
          preview tile, and starter categories for snippets, tools, references,
          and utility methods.
        </VibeMessage>

        <VibeMessage from="Ryan">
          very cool. Add that category UI you made for the above work to the
          Posts home page
        </VibeMessage>
        <VibeMessage from="Codex">
          Added the category UI to the Posts home page.
        </VibeMessage>

        <VibeMessage from="Ryan">
          This "Categories" UI is likely reusable. Let's extract a component
          that can be shared among different pages and use that on Posts home
          and Useful Tools and Code home pages.
          <br />
          <br />
          Furthermore, let's make those badges buttons (keeping the same UI),
          where clicking it will update the UI to some "on" version and filter
          the shown content to those matching the category selected, and
          clicking an active/"on" badge will remove the filter, going back to
          showing all the content.
        </VibeMessage>
        <VibeMessage from="Codex">
          I pulled the badge row into a shared category filter, turned the
          badges into accessible toggle buttons, and moved both pages to
          data-driven filtered previews.
        </VibeMessage>

        <VibeMessage from="Ryan">
          can we make the "Active"/"On" effect... cooler?
        </VibeMessage>
        <VibeMessage from="Codex">
          I replaced the plain active ring with a lifted badge, stronger
          saturation, layered shadow, and a floating check marker.
        </VibeMessage>

        <VibeMessage from="Ryan">
          I'm thinking we add an image for the "Nothing here yet." state (with
          alt text for a11y users).
          <br />
          <br />
          I'd like a geeky pop feel image that represents an empty place, a 4:3
          ratio would probably look good in the space.
        </VibeMessage>
        <VibeMessage from="Codex">
          I generated a 4:3 empty coding-stage illustration, saved it as a
          shared asset, and wrapped it in a reusable empty-state component with
          descriptive alt text.
        </VibeMessage>

        <VibeMessage from="Ryan">
          Very nice! Last thing I want to do in this session is create a posts
          titled "My first real vibe" (subtitled "Talking back and forth with my
          robot colleague") that will be a "how to" post.
          <br />
          <br />
          You can base this post on the "Hello issues!" post, and like in that
          post, I will be writing my own bit of content at the top (so give me a
          placeholder for that followed by a h3 "Everything written below was
          generated by OpenAI Codex", which is where I'll put content generated
          by you.
          <br />
          <br />
          That generated content will ideally cover my prompts and your
          responses (maybe with some text-message-esque styling in the UI to
          represent each side of the conversation). End with this final prompt
          and response with a handshaking emoji as the last standalone line
          (center aligned).
        </VibeMessage>
        <VibeMessage from="Codex">
          I added the new how-to post, routed it, made it the latest post, and
          shaped the generated section as a conversation recap with message-like
          styling.
        </VibeMessage>
      </div>

      <p>
        The pattern is simple, but it matters: ask for behavior, inspect the
        existing system, make the smallest useful change, verify, and keep
        talking. The more concrete the next prompt gets, the more the work
        starts to look like pair programming.
      </p>

      <p className="vibe-handshake" aria-label="handshake">
        🤝
      </p>
    </PostWrapper>
  );
}

export default MyFirstRealVibeScreen;
